/**
 * Pricing Page
 * 
 * Clear, conversion-focused pricing with defined limits.
 * 3 plans: Free, Investor ($49), Pro ($99)
 * Internal IDs: FREE, PRO, PRO_PLUS
 */

import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { useTranslation } from '../i18n';
import { billingAPI } from '../api';
import Header from '../components/Header';
import { Check, Zap, TrendingUp, Shield, Building2, Users, ArrowRight, ArrowLeft, Loader2, Star, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const Pricing = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { lang } = useTranslation();
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const handleUpgrade = async (planId) => {
    if (!user) {
      toast.info(lang === 'en' ? 'Sign in to upgrade' : 'Inicia sesión para mejorar tu plan');
      navigate('/auth/callback');
      return;
    }
    
    setCheckoutLoading(planId);
    try {
      const response = await billingAPI.createCheckout({
        plan_id: planId,
        user_id: user.user_id,
        origin_url: window.location.origin
      });
      
      // Backend returns the Stripe URL in `url` (billing_service). Read that first;
      // fall back to other names for safety. Surface a loud error if absent so a
      // silent dead click (the original bug) can never hide again.
      const checkoutUrl = response.data.url || response.data.checkout_url || response.data.session_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        console.error('Checkout: no URL in response', response.data);
        toast.error(lang === 'en' ? 'Could not open checkout' : 'No se pudo abrir el pago');
        setCheckoutLoading(null);
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error(lang === 'en' ? 'Failed to start checkout' : 'Error al iniciar pago');
    } finally {
      setCheckoutLoading(null);
    }
  };

  // 3 plans: Free, Investor ($49/50mo), Pro ($99/200mo)
  // Internal IDs: free→FREE, pro→PRO (displays as "Investor"), pro_plus→PRO_PLUS (displays as "Pro")
  const plans = [
    {
      id: 'free',
      internalId: 'FREE',
      name: lang === 'en' ? 'Free' : 'Gratis',
      tagline: lang === 'en' ? 'Get started analyzing deals' : 'Comienza a analizar tratos',
      price: 0,
      period: '',
      analyses: '3',
      analysesLabel: lang === 'en' ? 'lifetime analyses' : 'análisis de por vida',
      features: [
        lang === 'en' ? 'Full property analysis' : 'Análisis completo de propiedades',
        lang === 'en' ? 'ARV with comparable sales' : 'ARV con ventas comparables',
        lang === 'en' ? 'Investment metrics & scoring' : 'Métricas y puntuación',
        lang === 'en' ? 'PDF report export' : 'Exportar reporte PDF',
      ],
      cta: lang === 'en' ? 'Get Started Free' : 'Comenzar Gratis',
      highlighted: false,
      icon: Zap,
    },
    {
      id: 'pro',              // Internal ID maps to PRO in backend
      internalId: 'PRO',
      name: 'Investor',       // Display name
      tagline: lang === 'en' ? 'For active deal hunters' : 'Para buscadores activos de tratos',
      price: 49,
      period: lang === 'en' ? '/month' : '/mes',
      analyses: '50',
      analysesLabel: lang === 'en' ? 'analyses per month' : 'análisis por mes',
      features: [
        lang === 'en' ? 'Everything in Free' : 'Todo lo de Gratis',
        lang === 'en' ? '50 deal analyses monthly' : '50 análisis mensuales',
        lang === 'en' ? 'Save & compare deals' : 'Guardar y comparar tratos',
        lang === 'en' ? 'Add custom comparables' : 'Agregar comparables manuales',
        lang === 'en' ? 'Email support' : 'Soporte por email',
      ],
      cta: lang === 'en' ? 'Upgrade to Investor' : 'Mejorar a Investor',
      highlighted: true,
      popular: true,
      icon: TrendingUp,
    },
    {
      id: 'pro_plus',         // Internal ID maps to PRO_PLUS in backend
      internalId: 'PRO_PLUS',
      name: 'Pro',            // Display name
      tagline: lang === 'en' ? 'For serious investors' : 'Para inversores serios',
      price: 99,
      period: lang === 'en' ? '/month' : '/mes',
      analyses: '200',
      analysesLabel: lang === 'en' ? 'analyses per month' : 'análisis por mes',
      features: [
        lang === 'en' ? 'Everything in Investor' : 'Todo lo de Investor',
        lang === 'en' ? '200 deal analyses monthly' : '200 análisis mensuales',
        lang === 'en' ? 'Bulk deal comparison' : 'Comparación masiva de tratos',
        lang === 'en' ? 'Priority support' : 'Soporte prioritario',
        lang === 'en' ? 'Advanced analytics' : 'Analítica avanzada',
      ],
      cta: lang === 'en' ? 'Upgrade to Pro' : 'Mejorar a Pro',
      highlighted: false,
      icon: BarChart3,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          data-testid="pricing-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {lang === 'en' ? 'Back' : 'Regresar'}
        </button>

        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {lang === 'en' 
              ? 'Know if a deal works before risking your money' 
              : 'Sabe si un trato funciona antes de arriesgar tu dinero'}
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 mb-6">
            {lang === 'en' 
              ? 'Instant deal validation. Avoid bad deals in seconds.' 
              : 'Validación instantánea. Evita malos tratos en segundos.'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span>{lang === 'en' ? 'Real data, real results' : 'Datos reales, resultados reales'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Check className="w-4 h-4 text-emerald-500" />
              <span>{lang === 'en' ? 'No commitment required' : 'Sin compromiso'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Zap className="w-4 h-4 text-emerald-500" />
              <span>{lang === 'en' ? 'Results in seconds' : 'Resultados en segundos'}</span>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 max-w-4xl mx-auto justify-center">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isLoading = checkoutLoading === plan.id;
            
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-6 transition-all ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-xl shadow-blue-200 scale-105 z-10'
                    : 'bg-white border border-slate-200 shadow-sm hover:shadow-md'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-amber-400 text-amber-900 text-xs font-semibold rounded-full">
                      {plan.badge}
                    </span>
                  </div>
                )}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" /> {lang === 'en' ? 'Popular' : 'Popular'}
                    </span>
                  </div>
                )}
                
                {/* Plan Header */}
                <div className="mb-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                    plan.highlighted ? 'bg-white/20' : 'bg-slate-100'
                  }`}>
                    <Icon className={`w-5 h-5 ${plan.highlighted ? 'text-white' : 'text-indigo-600'}`} />
                  </div>
                  <h3 className={`text-xl font-bold ${plan.highlighted ? 'text-white' : 'text-slate-900'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm mt-1 ${plan.highlighted ? 'text-indigo-100' : 'text-slate-500'}`}>
                    {plan.tagline}
                  </p>
                </div>
                
                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    {plan.price > 0 ? (
                      <>
                        <span className={`text-3xl font-bold ${plan.highlighted ? 'text-white' : 'text-slate-900'}`}>
                          ${plan.price}
                        </span>
                        <span className={`text-sm ${plan.highlighted ? 'text-indigo-200' : 'text-slate-500'}`}>
                          {plan.period}
                        </span>
                      </>
                    ) : (
                      <span className={`text-3xl font-bold ${plan.highlighted ? 'text-white' : 'text-slate-900'}`}>
                        {lang === 'en' ? 'Free' : 'Gratis'}
                      </span>
                    )}
                  </div>
                  
                  {/* Analyses Count - Prominent */}
                  <div className={`mt-2 py-2 px-3 rounded-lg ${
                    plan.highlighted ? 'bg-white/10' : 'bg-indigo-50'
                  }`}>
                    <span className={`text-2xl font-bold ${plan.highlighted ? 'text-white' : 'text-indigo-600'}`}>
                      {plan.analyses}
                    </span>
                    <span className={`text-xs ml-1 ${plan.highlighted ? 'text-indigo-200' : 'text-indigo-600'}`}>
                      {plan.analysesLabel}
                    </span>
                  </div>
                </div>
                
                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                        plan.highlighted ? 'text-indigo-200' : 'text-emerald-500'
                      }`} />
                      <span className={`text-sm ${plan.highlighted ? 'text-indigo-100' : 'text-slate-600'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                
                {/* CTA Button */}
                <button
                  onClick={() => {
                    if (plan.id === 'free') {
                      navigate('/analyze');
                    } else {
                      handleUpgrade(plan.id);
                    }
                  }}
                  disabled={isLoading}
                  className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                    plan.highlighted
                      ? 'bg-white text-indigo-600 hover:bg-indigo-50'
                      : plan.id === 'free'
                        ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                  data-testid={`cta-${plan.id}`}
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      {plan.cta}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                {/* Refund terms, AT the decision, not buried in Terms section 10.
                    Cancelling stops the next charge and keeps access to the end of the
                    period already paid for — but nothing is refunded for the unused
                    part of it. A buyer should read that before clicking, not after. */}
                {plan.id !== 'free' && (
                  <p
                    className={`mt-3 text-[11px] leading-snug text-center ${
                      plan.highlighted ? 'text-indigo-100' : 'text-slate-500'
                    }`}
                    data-testid={`refund-terms-${plan.id}`}
                  >
                    {lang === 'en'
                      ? 'Cancel any time — access continues to the end of the period you have paid for. Unused time is not refunded.'
                      : 'Cancelá cuando quieras — el acceso sigue hasta el final del período que ya pagaste. El tiempo no usado no se reembolsa.'}
                  </p>
                )}
                {/* The subscription agreement is entered on THIS click (the next screen is
                    Stripe's). Auto-renewal, cancellation and refunds are Terms 5, 9 and 10. */}
                {plan.id !== 'free' && (
                  <p
                    className={`mt-2 text-[11px] leading-snug text-center ${
                      plan.highlighted ? 'text-indigo-100' : 'text-slate-500'
                    }`}
                    data-testid={`legal-consent-${plan.id}`}
                  >
                    {lang === 'en' ? 'By subscribing you agree to the ' : 'Al suscribirte aceptas los '}
                    <Link to="/terms" className="underline underline-offset-2 hover:opacity-80">
                      {lang === 'en' ? 'Terms of Service' : 'Términos de Servicio'}
                    </Link>
                    {lang === 'en' ? ' and the ' : ' y la '}
                    <Link to="/privacy" className="underline underline-offset-2 hover:opacity-80">
                      {lang === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
                    </Link>
                    .
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Value Proposition */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              {lang === 'en' ? 'Why Investors Trust CHEKSTE' : 'Por Qué los Inversores Confían en CHEKSTE'}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">
                  {lang === 'en' ? 'Avoid Bad Deals' : 'Evita Malos Tratos'}
                </h3>
                <p className="text-sm text-slate-600">
                  {lang === 'en' 
                    ? 'Know if a deal is good, marginal, or should be avoided—before you invest.' 
                    : 'Sabe si un trato es bueno, marginal, o debe evitarse—antes de invertir.'}
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">
                  {lang === 'en' ? 'Faster Decisions' : 'Decisiones Más Rápidas'}
                </h3>
                <p className="text-sm text-slate-600">
                  {lang === 'en' 
                    ? 'Get instant analysis with real market data. Make offers with confidence.' 
                    : 'Obtén análisis instantáneo con datos reales del mercado. Haz ofertas con confianza.'}
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="font-semibold text-slate-800 mb-2">
                  {lang === 'en' ? 'Protect Your Capital' : 'Protege Tu Capital'}
                </h3>
                <p className="text-sm text-slate-600">
                  {lang === 'en' 
                    ? 'Conservative analysis helps you avoid costly mistakes and maximize returns.' 
                    : 'Análisis conservador te ayuda a evitar errores costosos y maximizar retornos.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            {lang === 'en' ? 'Frequently Asked Questions' : 'Preguntas Frecuentes'}
          </h2>
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-2">
                {lang === 'en' ? 'What counts as an analysis?' : '¿Qué cuenta como un análisis?'}
              </h3>
              <p className="text-sm text-slate-600">
                {lang === 'en' 
                  ? 'Each new property address you analyze counts as one analysis. Re-classifying a saved deal with different criteria is free and does not count toward your limit.' 
                  : 'Cada nueva dirección que analices cuenta como un análisis. Re-clasificar un trato guardado con diferentes criterios es gratis y no cuenta hacia tu límite.'}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-2">
                {lang === 'en' ? 'Can I cancel anytime?' : '¿Puedo cancelar en cualquier momento?'}
              </h3>
              <p className="text-sm text-slate-600">
                {lang === 'en' 
                  ? 'Yes. All paid plans can be cancelled at any time. Your access continues until the end of your billing period.' 
                  : 'Sí. Todos los planes de pago pueden cancelarse en cualquier momento. Tu acceso continúa hasta el final de tu período de facturación.'}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-semibold text-slate-800 mb-2">
                {lang === 'en' ? 'What data sources do you use?' : '¿Qué fuentes de datos usan?'}
              </h3>
              <p className="text-sm text-slate-600">
                {lang === 'en' 
                  ? 'We use professional real estate data providers including property records, recent sales, and rental market data to give you accurate, up-to-date analysis.' 
                  : 'Usamos proveedores profesionales de datos inmobiliarios incluyendo registros de propiedades, ventas recientes, y datos del mercado de alquileres para darte análisis preciso y actualizado.'}
              </p>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-16 text-center">
          <p className="text-slate-600 mb-4">
            {lang === 'en' ? 'Ready to make smarter investment decisions?' : '¿Listo para tomar decisiones de inversión más inteligentes?'}
          </p>
          <button
            onClick={() => navigate('/analyze')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
            data-testid="final-cta"
          >
            {lang === 'en' ? 'Analyze Your First Deal Free' : 'Analiza Tu Primer Trato Gratis'}
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-xs text-slate-500 mt-2">
            {lang === 'en' ? '3 free analyses • No credit card required' : '3 análisis gratis • Sin tarjeta de crédito'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
